import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import CheckboxField from '../../components/Fields/CheckboxField';
import DateControl from '../../components/Fields/DateControl';
import DateField from '../../components/Fields/DateField';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import DesignPhaseModel from '../../models/DesignPhaseModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';

const DesignPhaseView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<DesignPhaseModel>({
        id: 0,
        name: '',
        isDefault: false,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let designPhaseId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        designPhaseId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (designPhaseId !== undefined) {
            DataService.getById<DesignPhaseModel>('api/v1/designphases/', designPhaseId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [designPhaseId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<DesignPhaseModel>(`api/v1/designphases/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<DesignPhaseModel>(`api/v1/designphases/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'isDefault', label: 'Default phase' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (designPhaseId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditDesignPhase)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Design phase' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditDesignPhase}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                            <CheckboxField {...getFieldProps('isDefault')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default DesignPhaseView;